import { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import MastercardIcon from '@assets/icons/mastercard.svg';
import { EAccountType } from '@entities/accounts/model';
import {
  EDirection,
  ETransactionStatus,
  ETransactionType,
  IOperation,
} from '@entities/transactions/model';
import { cn } from '@lib/utils';
import { routesMap } from '@shared/lib';
import clsx from 'clsx';
import { format as formatHelper } from 'date-fns';

import TransactionBadge from './table/transaction-badge';
import TransactionCard from './table/transaction-card';

export const formatDate = (
  dateString: string,
  format: 'full' | 'short' | 'time' | 'date' = 'full',
) => {
  const date = new Date(dateString);
  const optionsFull: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const optionsShort: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  const isYearDisplayed = new Date().getFullYear() !== date.getFullYear();

  switch (format) {
    case 'full':
      return date.toLocaleDateString('en-GB', optionsFull);
    case 'short':
      return date.toLocaleDateString('en-GB', optionsShort).toUpperCase();
    case 'time':
      return date.toLocaleTimeString('en-GB', optionsTime).toLowerCase();
    case 'date':
      return formatHelper(date, `MMMM dd${isYearDisplayed ? ' yyyy' : ''}`);
    default:
      return date.toLocaleDateString('en-GB', optionsFull);
  }
};

export const formatUnmaskedCardNumber = (cardNumber: string): string => {
  return cardNumber.replace(/\d{4}(?=.)/g, '$& ');
};

export const getFormattedCardNumber = (
  cardNumber: string,
  noCardPrefix = false,
) => {
  const lastFourDigits = cardNumber?.slice(-4) || '';
  return `${noCardPrefix ? '' : 'CARD'} •••• ${lastFourDigits}`;
};

export const getDescription = (
  operation?: IOperation,
  styles?: string,
  checking = false,
) => {
  if (!operation) return '';

  if (operation.type === ETransactionType.CARD && operation.card) {
    const path = routesMap.cards.full + '/' + operation.card.id;

    return (
      <Link to={path}>
        <TransactionCard
          className={cn('mx-auto', styles)}
          card={MastercardIcon}
          number={operation.card.number}
        />
      </Link>
    );
  }

  if (operation.type === ETransactionType.EXCHANGE) {
    const debitAccountAddress = operation.fromAccount?.address ?? '';

    const currencyToDisplay = debitAccountAddress.includes('system:')
      ? operation.account.currency
      : operation.fromAccount?.currency;

    const type = operation.account.type;
    let path: string;

    if (type === EAccountType.CHECKING) {
      path = routesMap.accounts.full + '/' + operation.account.id;
    } else {
      path = routesMap.savings.full + '/' + operation.account.id;
    }

    return (
      <Link to={path} className="flex items-center gap-1">
        {checking && <span>checking</span>}
        <TransactionCard
          className={cn('mx-auto', clsx({ 'w-max': checking }), styles)}
          currency={currencyToDisplay}
        />
      </Link>
    );
  }

  const currencyToDisplay = operation.account.currency;

  const type = operation.account.type;
  let path: string;
  const route = operation.account.id;

  if (type === EAccountType.CHECKING) {
    path = routesMap.accounts.full + '/' + route;
  } else {
    path = routesMap.savings.full + '/' + route;
  }

  return (
    <Link to={path} className="flex items-center gap-1">
      {checking && <span>checking</span>}
      <TransactionCard
        className={cn('mx-auto', clsx({ 'w-max': checking }), styles)}
        currency={currencyToDisplay}
      />
    </Link>
  );
};

export const getExchangeAccounts = (operation?: IOperation) => {
  if (operation?.type !== ETransactionType.EXCHANGE) return;

  const fromOperation = operation.fromAccount;
  const toOperation = operation.account;

  const from = fromOperation;
  const to = toOperation;

  if (!from && !to) return;

  return {
    from,
    to,
    fromOperation,
    toOperation,
  };
};

export const groupByDate = (transactionGroups: IOperation[]) => {
  return transactionGroups.reduce(
    (acc: { [key: string]: IOperation[] }, group) => {
      const date = formatDate(group.createdAt, 'date');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(group);
      return acc;
    },
    {},
  );
};

export const getStatus = (operation: IOperation, styles?: string) => {
  const fromValue = operation?.fromAmount;
  const fromCurrency = operation?.fromAccount?.currency;

  const pendingBadge = (
    <TransactionBadge className={cn('pointer-events-none', styles)}>
      Pending
    </TransactionBadge>
  );

  const rejectedBadge = (
    <TransactionBadge className={cn('pointer-events-none', styles)}>
      Rejected
    </TransactionBadge>
  );

  const completedBadge = (operation.type === ETransactionType.WITHDRAW ||
    operation.type === ETransactionType.DEPOSIT) && (
    <TransactionBadge className={cn('pointer-events-none', styles)}>
      Completed
    </TransactionBadge>
  );

  const displayedStatus = {
    [ETransactionStatus.PENDING]: pendingBadge,
    [ETransactionStatus.REJECTED]: rejectedBadge,
    [ETransactionStatus.COMPLETE]: completedBadge,
  };

  const displayedAmount = operation.type === ETransactionType.EXCHANGE && (
    <TransactionBadge className={cn('pointer-events-none', styles)}>
      from {fromValue} {getCurrencySymbol(fromCurrency ?? 'USD')}
    </TransactionBadge>
  );

  if (displayedAmount && displayedStatus[operation.status]) {
    return (
      <>
        {displayedAmount}
        {displayedStatus[operation.status]}
      </>
    );
  }

  if (displayedAmount) {
    return displayedAmount;
  }

  if (displayedStatus[operation.status]) {
    return displayedStatus[operation.status];
  }
};

export const getTitle = (
  transactionGroup: IOperation,
  showStatus: boolean = true,
) => {
  const operation = transactionGroup;
  if (!operation) return 'Unknown Transaction';

  const { transferComment } = operation;

  const formattedType = operation.type.replace('_', ' ');

  const status = getStatus(operation);

  const titleStyles =
    'max-w-[500px] overflow-hidden w-full text-ellipsis whitespace-nowrap';

  if (operation.type === ETransactionType.CARD) {
    return (
      <>
        <p className={titleStyles}>{transferComment || formattedType}</p>
        {showStatus && status}
      </>
    );
  }

  if (operation.type === ETransactionType.EXCHANGE) {
    return (
      <>
        <p className={titleStyles}>{ETransactionType.EXCHANGE} </p>
        {showStatus && status}
      </>
    );
  }

  return (
    <>
      <p className={titleStyles}>{formattedType}</p>
      {showStatus && status}
    </>
  );
};

export const getAmount = (
  transactionGroup?: IOperation,
  as: 'string' | 'node' = 'node',
) => {
  const operation = transactionGroup;
  if (!operation) {
    return {
      formattedValue: '0',
      amount: 0,
      currency: '',
    };
  }

  let amount = 0;
  let formattedValue: string | ReactElement = '0';
  let currency = '';

  if (operation.type === ETransactionType.EXCHANGE) {
    amount = Number(operation.fromAmount);
    currency = operation.fromAccount?.currency ?? 'USD';
    const receivedAmount = Number(operation.amount) || 0;
    const receivedCurrency = operation.account?.currency || '';
    formattedValue = `-${formatBalance(amount)} ${currency} / +${formatBalance(receivedAmount)} ${getCurrencySymbol(receivedCurrency)}`;
  } else {
    const direction = operation.amount > 0 ? EDirection.IN : EDirection.OUT;
    if (direction === EDirection.IN) {
      amount = Number(operation.amount);
      currency = operation.account.currency;
      formattedValue =
        as === 'string' ? (
          `+${formatBalance(amount)} ${getCurrencySymbol(currency)}`
        ) : (
          <>
            +{formatBalance(amount)}{' '}
            <span className="text-neutral04">
              {getCurrencySymbol(currency)}
            </span>
          </>
        );
    } else {
      amount = Number(operation.amount);
      currency = operation.account.currency;
      formattedValue =
        as === 'string' ? (
          `${formatBalance(amount)} ${getCurrencySymbol(currency)}`
        ) : (
          <>
            {formatBalance(amount)}{' '}
            <span className="text-neutral04">
              {getCurrencySymbol(currency)}
            </span>
          </>
        );
    }
  }

  return {
    formattedValue,
    amount,
    currency,
  };
};

export const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    // Actually we don't need RUB at all (with prod data)
    RUB: 'RUB',
    // Add more currency symbols as needed
  };
  return symbols[currency] || currency;
};

export const formatBalance = (balance: number): string => {
  const parts = (+balance).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return parts.join('.');
};

export const formatBalanceWithCurrency = ({
  balance,
  currency,
  withoutSpaceBetweenBalanceAndCurrency,
}: {
  balance: number;
  currency: string;
  withoutSpaceBetweenBalanceAndCurrency?: boolean;
}) => {
  const formattedBalance = formatBalance(balance);
  const currencySymbol = getCurrencySymbol(currency);

  if (balance === 0) {
    return `${currencySymbol}0`;
  }

  if (withoutSpaceBetweenBalanceAndCurrency) {
    return `${currencySymbol}${formattedBalance}`;
  }

  return `${currencySymbol} ${formattedBalance}`;
};
